import { dropExpiredPromotions, nextPromotionBoundary, parsePublishedPromotion, type PublishedPromotion } from '../utils/promotions';

// One shared, reference-counted subscription to published promotions for the
// whole app (the feed section and the collection screen both read from it —
// never a listener per card or per screen). Platform pieces are injected so
// the scheduling/caching logic is unit-testable without Firebase.

export type PromotionDoc = { id: string; data: Record<string, unknown> };

export type PromotionsSource = {
  subscribe(onDocs: (docs: PromotionDoc[]) => void, onError: (error: unknown) => void): () => void;
};

export type PromotionsCacheStorage = {
  getItem(key: string): Promise<string | null>;
  setItem(key: string, value: string): Promise<void>;
};

export type ForegroundSource = { onForeground(callback: () => void): () => void };

export type PromotionsState = {
  promotions: PublishedPromotion[];
  // true once either the live listener or the cache has answered
  loaded: boolean;
  // true while showing the persisted copy before the listener's first answer
  fromCache: boolean;
  error: string | null;
  // bumped at every start/end boundary and on foreground, so consumers
  // re-evaluate eligibility against a fresh clock
  clock: number;
};

type Deps = {
  source: PromotionsSource;
  storage: PromotionsCacheStorage;
  foreground: ForegroundSource;
  now?: () => number;
  setTimer?: (callback: () => void, ms: number) => unknown;
  clearTimer?: (handle: unknown) => void;
  cacheKey?: string;
};

const MAX_TIMER_MS = 2 ** 31 - 1;
// Fire a hair after the boundary so `now >= startAt` / `now >= endAt` holds.
const BOUNDARY_SLACK_MS = 250;

export function createPromotionsStore(deps: Deps) {
  const now = deps.now ?? (() => Date.now());
  const setTimer = deps.setTimer ?? ((cb, ms) => setTimeout(cb, ms));
  const clearTimer = deps.clearTimer ?? (handle => clearTimeout(handle as ReturnType<typeof setTimeout>));
  const cacheKey = deps.cacheKey ?? '@tonyte/promotions-v1';

  let state: PromotionsState = { promotions: [], loaded: false, fromCache: false, error: null, clock: now() };
  const listeners = new Set<(next: PromotionsState) => void>();
  let refCount = 0;
  let unsubscribeSource: (() => void) | null = null;
  let unsubscribeForeground: (() => void) | null = null;
  let boundaryTimer: unknown = null;
  let liveAnswered = false;

  const emit = (patch: Partial<PromotionsState>) => {
    state = { ...state, ...patch };
    listeners.forEach(listener => listener(state));
  };

  const scheduleBoundary = () => {
    if (boundaryTimer !== null) clearTimer(boundaryTimer);
    boundaryTimer = null;
    if (!refCount) return;
    const next = nextPromotionBoundary(state.promotions, now());
    if (next === null) return;
    boundaryTimer = setTimer(() => {
      boundaryTimer = null;
      tick();
    }, Math.min(MAX_TIMER_MS, Math.max(0, next - now()) + BOUNDARY_SLACK_MS));
  };

  // Re-evaluate against the current time: drop anything that has ended (so a
  // cached or stale copy can never outlive endAt) and wake consumers.
  const tick = () => {
    const t = now();
    emit({ promotions: dropExpiredPromotions(state.promotions, t), clock: t });
    scheduleBoundary();
  };

  const persist = (docs: PromotionDoc[]) => {
    const serializable = docs.map(doc => ({ id: doc.id, data: toPlain(doc.data) }));
    deps.storage.setItem(cacheKey, JSON.stringify(serializable)).catch(() => {});
  };

  const handleDocs = (docs: PromotionDoc[]) => {
    liveAnswered = true;
    const t = now();
    const promotions = dropExpiredPromotions(
      docs.map(doc => parsePublishedPromotion(doc.id, doc.data)).filter((p): p is PublishedPromotion => Boolean(p)),
      t,
    );
    emit({ promotions, loaded: true, fromCache: false, error: null, clock: t });
    persist(docs);
    scheduleBoundary();
  };

  const handleError = (error: unknown) => {
    // Keep whatever is showing (cache or last live answer); retry on the next
    // foreground. A failed listener must never blank or block the feed.
    unsubscribeSource?.();
    unsubscribeSource = null;
    emit({ loaded: true, error: error instanceof Error ? error.message : String(error) });
  };

  const startSource = () => {
    if (unsubscribeSource) return;
    try {
      unsubscribeSource = deps.source.subscribe(handleDocs, handleError);
    } catch (error) {
      handleError(error);
    }
  };

  const hydrateFromCache = async () => {
    try {
      const raw = await deps.storage.getItem(cacheKey);
      if (!raw || liveAnswered) return;
      const docs = JSON.parse(raw) as PromotionDoc[];
      const t = now();
      const promotions = dropExpiredPromotions(
        (Array.isArray(docs) ? docs : [])
          .map(doc => parsePublishedPromotion(doc?.id, doc?.data))
          .filter((p): p is PublishedPromotion => Boolean(p)),
        t,
      );
      if (liveAnswered) return;
      emit({ promotions, loaded: true, fromCache: true, clock: t });
      scheduleBoundary();
    } catch {
      // Corrupt cache — the live listener will answer.
    }
  };

  const acquire = () => {
    refCount += 1;
    if (refCount > 1) return;
    void hydrateFromCache();
    startSource();
    unsubscribeForeground = deps.foreground.onForeground(() => {
      if (!unsubscribeSource) startSource();
      tick();
    });
    scheduleBoundary();
  };

  const release = () => {
    refCount = Math.max(0, refCount - 1);
    if (refCount) return;
    unsubscribeSource?.();
    unsubscribeSource = null;
    unsubscribeForeground?.();
    unsubscribeForeground = null;
    if (boundaryTimer !== null) clearTimer(boundaryTimer);
    boundaryTimer = null;
    liveAnswered = false;
  };

  return {
    getState: () => state,
    subscribe(listener: (next: PromotionsState) => void) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    acquire,
    release,
    tick,
    // test/debug visibility
    get activeSubscriptions() {
      return unsubscribeSource ? 1 : 0;
    },
  };
}

// Firestore Timestamps -> epoch ms so the cache round-trips through JSON.
function toPlain(value: unknown): unknown {
  if (value && typeof value === 'object') {
    const raw = value as { toMillis?: () => number };
    if (typeof raw.toMillis === 'function') return raw.toMillis();
    if (Array.isArray(value)) return value.map(toPlain);
    return Object.fromEntries(Object.entries(value as Record<string, unknown>).map(([k, v]) => [k, toPlain(v)]));
  }
  return value;
}

export type PromotionsStore = ReturnType<typeof createPromotionsStore>;
